import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import FormInput from '../../../components/FormInput';
import Toast from 'react-native-toast-message';
import { deacService } from '../../../services/central-api/deac';
import DefinedColorPicker from '../../../components/DefinedColorPicker';
import Badge from '../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../styles/colorPalettes';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedVacancyTagId: number | undefined;
    onConfirm: () => void;
}

export default function VacancyTagModal({ selectedVacancyTagId, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState<string>('');
    const [color, setColor] = useState<string>('');
    const [textColor, setTextColor] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetchingData, setIsFetchingData] = useState<boolean>(selectedVacancyTagId && selectedVacancyTagId > 0 ? true : false);

    async function getVacancyTag() {
        try {
            setIsFetchingData(true);
            const vacancyTag = await deacService.getVacancyTagById(Number(selectedVacancyTagId));
            setName(vacancyTag.name ?? '');
            setColor(vacancyTag.color ?? '');

            const index = DEFAULT_PASTEL_COLORS.indexOf(vacancyTag.color);
            setTextColor(DEFAULT_PASTEL_TEXT_COLORS[index]);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            closeModal();
        } finally {
            setIsFetchingData(false);
        }
    }

    async function formSubmit() {
        try {
            setIsLoading(true);

            if (selectedVacancyTagId) {
                await deacService.updateVacancyTag({ id: selectedVacancyTagId, name, color });
            } else {
                await deacService.createVacancyTag({ name, color });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isModalVisible && selectedVacancyTagId && selectedVacancyTagId > 0) {
            getVacancyTag();
        }
    }, [selectedVacancyTagId, isModalVisible]);

    function isFormValid(): boolean {
        if (name == '' || color == '') {
            return false;
        }

        return true;
    }

    const closeModal = () => {
        setModalVisible(false);
        setName('');
        setColor('');
        setTextColor('');
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    {isFetchingData ?
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        </View>
                        : <>
                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.header}>{translate('tagRegistration')}</MyAppText>
                                <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                            </View>
                            <View style={{ flexDirection: 'column', rowGap: 16 }}>
                                <FormInput
                                    labelStyle={styles.inputLabelStyle}
                                    onChangeText={(value) => setName(value)}
                                    value={name}
                                    label={translate('title')}
                                    maxCharacters={50}
                                />
                                <DefinedColorPicker
                                    colors={DEFAULT_PASTEL_COLORS}
                                    textColors={DEFAULT_PASTEL_TEXT_COLORS}
                                    label={translate('tagColor')}
                                    selectedColor={color}
                                    onColorSelect={setColor}
                                    onColorSelectText={setTextColor}
                                />
                                {/* Preview */}
                                <View style={styles.previewContainer}>
                                    <MyAppText style={styles.previewText}>{translate('preview')}</MyAppText>
                                    {isFormValid()
                                        ? <Badge mode='ROUNDED' backgroundColor={color} textColor={textColor} >{name}</Badge>
                                        : <MyAppText style={styles.previewNoContent} >{translate('fillPreviousFields')}</MyAppText>}
                                </View>
                            </View>
                            <FormActions
                                viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                                buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                                buttonContentContainerStyle={{ flex: 1 }}
                                onSubmit={formSubmit}
                                onClose={closeModal}
                                disabled={!isFormValid() || isLoading}
                                isLoading={isLoading}
                            />
                        </>}
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
    previewContainer: {
        flexDirection: 'column',
        rowGap: 8,
    },
    previewText: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.primary['500']
    },
    previewNoContent: {
        fontSize: 16,
        fontWeight: '400',
        color: theme.colors.neutral['400']
    }
}));
